import React, {useCallback, useMemo} from 'react';
import {useProductContext} from 'contexts';
import {Col, Container, Row} from 'react-bootstrap';
import excel from 'shared/Excel';
import {useFormikContext} from 'formik';
import {PreviewProvider} from 'components/customer/Product/Preview/PreviewContext';
import {FieldSet, FIELDSETS} from 'components/customer/Product/FieldSet';
import {Fieldset} from 'components/customer/Product/entity/Fieldset';
import {Structure} from 'components/customer/Product/entity/Structure';
import {ProductDataStore} from 'components/customer/Preview3D/usePreview3DData';
import {PreviewFormValues} from 'components/customer/Preview3D/types';
import {MaterialOptions} from 'shared/types';
import {useSpecPreview} from 'components/customer/Product/helpers/useSpecPreview';
import {use3dPreview} from 'components/customer/Product/helpers/use3dPreview';
import {useMiniPreview} from 'components/customer/Product/helpers/useMiniPreview';
import {DrawerHardware} from 'components/customer/Product/Specs/DrawerHardware';
import {DrawerDefaultsProvider} from 'components/customer/Product/Drawer/DrawerDefaultsContext';

interface SpecsProps {
    title: string;
    preview: string;
    fieldsets: Fieldset[];
    structure: Structure;
    isQFP?: boolean;
    className?: string;
}

// fieldsets grouped into the Drawer Hardware card, in display order
const HARDWARE_FIELDSETS = [
    FIELDSETS.DRAWER_FIELDS,
    FIELDSETS.DRAWER_MARGINS,
    FIELDSETS.BORDER_WIDTHS,
    FIELDSETS.DOOR_EDGES,
];

export interface ProductContextType {
    productDataStore: ProductDataStore;
    getMaterialOptions: () => MaterialOptions;
    isLoading: boolean;
    formStructure: Structure[];
    currentTab: number;
    isMiniBrowser: boolean;
    doorPreviewOpacity: number;
    setDoorPreviewOpacity: (opacity: number) => void;
    setPositionType: (name: string, type: string) => void;
    resetNewShelf: () => void;
    newShelfStore: React.RefObject<{autoPosition: boolean; position: number}>;
    setHeightReference: (key: string, value: string) => void;
    shelvesHeightUpdated: boolean;
    shelvesHeightReference: React.RefObject<{[key: string]: string}>;
    simpleShelvesReference: number;
    shelvesUpdatedReference: React.RefObject<{[key: string]: boolean}>;
    adjustableLegsQuantity: number;
}

export const Specs = ({
    title,
    preview = 'standard',
    fieldsets,
    structure,
    isQFP = false,
    className = null,
}: SpecsProps) => {
    const {
        productDataStore,
        getMaterialOptions,
        formStructure,
        currentTab,
        isMiniBrowser,
        doorPreviewOpacity,
        setDoorPreviewOpacity,
        adjustableLegsQuantity,
    } = useProductContext<ProductContextType>();
    const {values, setFieldValue} = useFormikContext<PreviewFormValues>();

    const checkCustomDoorHang = (doorHangName: string) => {
        const doorFields = fieldsets.find(
            (fieldset) => fieldset.name === 'door_fields'
        );

        if (doorFields) {
            const doorHang = doorFields?.fields?.find(
                (doorField) => doorField.name === 'door_hang'
            );

            if (doorHang) {
                return (
                    (doorHang.value as string)
                        ?.toLowerCase()
                        ?.indexOf(doorHangName) > -1
                );
            }
        }

        return false;
    };

    const hasBifoldDoor = useMemo(() => {
        return checkCustomDoorHang('bifold');
    }, [fieldsets]);

    const hasRollUpDoor = useMemo(() => {
        return checkCustomDoorHang('rollup');
    }, [fieldsets]);

    const {panelPreview, hasPreview} = useSpecPreview(
        title,
        preview,
        structure,
        fieldsets,
        isQFP
    );
    const {
        isSpecsTab,
        has3DPreview,
        show3DPreview,
        panel3DPreview,
        hiddenScreenshotPreview,
    } = use3dPreview(fieldsets, hasBifoldDoor, hasRollUpDoor);
    useMiniPreview(
        title,
        panelPreview,
        panel3DPreview,
        !!show3DPreview,
        hasPreview
    );

    const filteredFieldsets = fieldsets
        .sort((fieldsetA, fieldsetB) => {
            if (title === 'Drawers') {
                const a = fieldsetA.name;
                const b = fieldsetB.name;

                if (a < b) return 1;
                else return -1;
            }

            return 0;
        })
        .filter((fieldset) => {
            if (
                fieldset.hasOwnProperty('options') &&
                fieldset.options.hasOwnProperty('visible')
            ) {
                let visibleCondition = fieldset.options.visible;

                if (
                    !isNaN(Number(visibleCondition)) &&
                    typeof visibleCondition !== 'boolean'
                ) {
                    visibleCondition = parseInt(String(visibleCondition)) > 0;
                }

                if (
                    isNaN(Number(visibleCondition)) &&
                    typeof visibleCondition === 'string'
                ) {
                    try {
                        visibleCondition = excel.calculate(
                            visibleCondition,
                            values
                        );
                    } catch (error) {
                        visibleCondition = fieldset.options.visible;
                    }
                }

                if (typeof visibleCondition === 'boolean') {
                    if (!visibleCondition) return false;
                } else {
                    const isVisible =
                        !isQFP && getMaterialOptions()?.cabinet_door?.advanced;

                    if (!isVisible) return false;
                }
            }

            return true;
        });

    const fieldValueHandler = useCallback(
        (name: string, value: string | number | boolean) => {
            void setFieldValue(name, value);
        },
        [setFieldValue]
    );

    const renderFieldSet = (fieldset: Fieldset, index: number) => {
        return (
            <FieldSet
                key={index}
                index={index}
                hasPreview={hasPreview || has3DPreview}
                fieldset={fieldset}
                values={values}
                showFieldsetTitle={
                    fieldset.name !== FIELDSETS.MATERIALS &&
                    fieldset.name !== FIELDSETS.DRAWER_FACES
                }
                setFieldValue={fieldValueHandler}
            />
        );
    };

    let drawerRunners = null;
    let additionals = null;
    let drawerHardware: Fieldset[] = [];
    const drawerRunnersFilter = (fieldset: Fieldset) =>
        fieldset.name == 'drawer_runners' &&
        !fieldset?.options?.hasOwnProperty('innerDrawer');
    const hardwareFilter = (fieldset: Fieldset) =>
        drawerHardware.includes(fieldset);
    const additionalFilter = (fieldset: Fieldset) =>
        fieldset.name == 'additional' || fieldset.name == 'variations';

    if (title === 'Drawers') {
        drawerRunners = filteredFieldsets.filter(drawerRunnersFilter);
        drawerHardware = HARDWARE_FIELDSETS.map((name) =>
            filteredFieldsets.find((fieldset) => fieldset.name === name)
        ).filter(Boolean);
    }

    if (isSpecsTab) additionals = filteredFieldsets.filter(additionalFilter);

    const isMaterialTab = fieldsets.find(
        (fieldset) => fieldset.name === 'materials'
    );

    return (
        <Container className={className == null ? '' : className}>
            <Row>
                <DrawerDefaultsProvider>
                    <Col md={isMaterialTab ? 12 : undefined}>
                        <DrawerHardware
                            fieldsets={drawerHardware}
                            hasPreview={hasPreview || has3DPreview}
                            runnerFieldset={drawerRunners?.[0]}
                        />
                        {drawerRunners === null
                            ? null
                            : drawerRunners.map(renderFieldSet)}
                        {filteredFieldsets
                            .filter(
                                (fieldset) => !drawerRunnersFilter(fieldset)
                            )
                            .filter((fieldset) => !additionalFilter(fieldset))
                            .filter((fieldset) => !hardwareFilter(fieldset))
                            .map(renderFieldSet)}
                    </Col>
                </DrawerDefaultsProvider>
                {isMiniBrowser ? null : show3DPreview && panel3DPreview ? (
                    panel3DPreview
                ) : (
                    <PreviewProvider
                        product={values}
                        formStructure={formStructure}
                        currentTab={currentTab}
                        doorPreviewOpacity={doorPreviewOpacity}
                        setDoorPreviewOpacity={setDoorPreviewOpacity}
                        productDataStore={productDataStore}
                        isMiniBrowser={isMiniBrowser}
                        getMaterialOptions={getMaterialOptions}
                        adjustableLegsQuantity={adjustableLegsQuantity}>
                        {panelPreview}
                    </PreviewProvider>
                )}
            </Row>
            {additionals ? (
                <Row style={{marginTop: 20}}>
                    <Col xs={12}>{additionals.map(renderFieldSet)}</Col>
                </Row>
            ) : null}
            {hiddenScreenshotPreview}
        </Container>
    );
};
